import { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFichierSecurise } from '../utils/fichiersSecurises';

const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
const VERT = '#1B5E20';

/**
 * PhotoProfil — affiche la photo de l'employé ou ses initiales.
 * Props :
 *   employe     : objet { id, prenom, nom, photo_url }
 *   taille      : number (px), défaut 72
 *   editable    : bool, affiche bouton upload au survol (défaut false)
 *   onPhotoMaj  : callback(photo_url) appelé après upload réussi
 *   dark        : bool, fond sombre (initiales en blanc semi-transparent)
 *   clair       : bool, langage visuel SOGAS RH (initiales vertes sur fond vert pâle, bordure fine)
 */
export default function PhotoProfil({ employe, taille = 72, editable = false, onPhotoMaj, dark = false, clair = false }) {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [hover, setHover] = useState(false);
  const inputRef = useRef(null);

  const initiales = [employe?.prenom?.[0], employe?.nom?.[0]]
    .filter(Boolean).join('').toUpperCase() || '?';

  // Photo chargée par l'API authentifiée (correctif sécurité : plus d'URL publique) ;
  // initiales affichées tant qu'elle n'est pas disponible ou si l'accès est refusé.
  const photoUrl = useFichierSecurise(employe?.photo_url || null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !employe?.id) return;
    try {
      setUploading(true);
      const form = new FormData();
      form.append('photo', file);
      const res = await axios.post(`${API}/employes/${employe.id}/photo`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (onPhotoMaj) onPhotoMaj(res.data.photo_url);
    } catch (err) {
      console.error('Upload photo:', err);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const overlayVisible = editable && (hover || uploading);

  return (
    <div
      style={{ position: 'relative', width: taille, height: taille, flexShrink: 0, cursor: editable ? 'pointer' : 'default' }}
      onMouseEnter={() => editable && setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => editable && !uploading && inputRef.current?.click()}
      title={editable ? 'Cliquer pour changer la photo' : undefined}
    >
      {/* Photo ou initiales */}
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={initiales}
          style={{
            width: taille, height: taille, borderRadius: '50%',
            objectFit: 'cover',
            border: dark ? '3px solid rgba(255,255,255,0.4)' : clair ? '1px solid var(--sg-bord)' : `2px solid ${VERT}`,
            display: 'block'
          }}
        />
      ) : (
        <div style={{
          width: taille, height: taille, borderRadius: '50%',
          background: dark ? 'rgba(255,255,255,0.2)' : clair ? 'var(--sg-primaire-doux)' : VERT,
          border: dark ? '3px solid rgba(255,255,255,0.4)' : 'none',
          color: clair ? 'var(--sg-primaire)' : '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: Math.round(taille * 0.36), fontWeight: clair ? 500 : 700,
          userSelect: 'none',
        }}>
          {initiales}
        </div>
      )}

      {/* Overlay upload au survol */}
      {editable && (
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 4,
          opacity: overlayVisible ? 1 : 0,
          transition: 'opacity 0.18s',
          pointerEvents: overlayVisible ? 'auto' : 'none',
        }}>
          {uploading ? (
            <span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>...</span>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <span style={{ color: '#fff', fontSize: 9, fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>Photo</span>
            </>
          )}
        </div>
      )}

      {/* Input file caché — accept images + capture=camera sur mobile */}
      {editable && (
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      )}
    </div>
  );
}
